// Komponent React ekwiwalent Blazor HydraAIAssistant

import React, { memo, forwardRef, useImperativeHandle } from 'react';
import classnames from 'classnames';

import { Select } from 'components/controls';
import { HydraAIAssistantProps, AssistantInfo } from 'components/containers/ChatBase/HydraAIAssistant.types';
import { useHydraAIAssistant } from 'components/containers/ChatBase//hooks';
import { ParsedMessage } from 'components/containers/ChatBase/helpers';

import { Trash, ArrowCounterclockwise, Paperclip, Send, Stop } from 'react-bootstrap-icons';
import styles from 'theme/components/containers/ChatBase/HydraAIAssistant.module.scss';
import { isWiseAiEnabled } from 'config/wiseAi';
import { WiseAiMessages } from 'components/containers/ChatBase/WiseAi/WiseAiMessages';

// Interface dla imperative handle
export interface HydraAIAssistantHandle {
  sendQuickMessage: (message: string) => void;
}

// Memoizowany komponent bąbelka wiadomości, aby zapobiec niepotrzebnym re-renderom
/* eslint-disable react/prop-types */
interface MessageBubbleProps {
  message: ParsedMessage;
}

const MessageBubble: React.FC<MessageBubbleProps> = memo(
  ({ message }) => (
    <div className={`message-${message.type}`}>
      <div
        className="message-bubble"
        dangerouslySetInnerHTML={{ __html: message.content }}
      />
    </div>
  ),
  (prevProps, nextProps) => 
    prevProps.message.id === nextProps.message.id && 
    prevProps.message.content === nextProps.message.content
);

MessageBubble.displayName = 'MessageBubble';

const HydraAIAssistant = forwardRef<HydraAIAssistantHandle, HydraAIAssistantProps>(({
  availableAssistants = [],
  urlParameters = {},
  isFullscreen = false,
  apiBaseUrl
}, ref) => {
/* eslint-enable react/prop-types */
  const {
    // State
    userMessage,
    setUserMessage,
    conversationContent,
    selectedAssistant,
    isProcessing,
    includeDetails,
    setIncludeDetails,
    attachedFiles,
    setAttachedFiles,
    showFileUpload,
    setShowFileUpload,
    filteredAssistants,
    isIframeFullscreen,

    // Refs
    chatDivRef,
    textAreaRef,
    assistantDropdownRef,
    fileInputRef,
    
    // Computed
    isDeveloperMode,
    showAssistantDropdown,
    attachedFilesCount,
    
    // Functions
    sendMessage,
    stopProcessing,
    startNewThread,
    removeLastMessage,
    selectAssistant,
    canCurrentAssistantAttachFiles,
    handleFileChange,
    handleFileRemoved,
    handleKeyDown,
    loadHistory,
    parsedMessages: messages,
    structuredMessages,
    t
  } = useHydraAIAssistant({
    availableAssistants,
    urlParameters,
    apiBaseUrl
  });

  // Udostępnij metodę sendQuickMessage przez ref
  useImperativeHandle(ref, () => ({
    sendQuickMessage: (message: string) => {
      sendMessage(message);
    }
  }), [sendMessage]);

  // Wrapper dla onClick - bez parametrów
  const handleSendClick = () => {
    sendMessage();
  };

  return (
    <div
      className={classnames(styles.hydraAIAssistant, {
        [styles.fullscreen]: isFullscreen || isIframeFullscreen,
        [styles.wiseAiVariant]: isWiseAiEnabled()
      })}
      style={{
        overflow: 'hidden',
        backgroundColor: 'var(--rz-sidebar-background-color, #f5f5f5)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {showAssistantDropdown && (
        <div
          className={styles.assistantDropdownContainer}
          style={{
            borderBottom: 'var(--rz-panel-menu-item-border, 1px solid #e0e0e0)',
            width: '100%',
            height: '64px',
            padding: '0.5rem',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
          ref={assistantDropdownRef}>
          <div className={styles.hoverBorder} style={{ width: '100%' }}>
            <Select<AssistantInfo>
              placeholder={t('Select Assistant')}
              value={selectedAssistant}
              onChange={(assistant: AssistantInfo | null) => {
                if (assistant) {
                  selectAssistant(assistant.name);
                }
              }}
              options={filteredAssistants.map((assistant) => ({
                value: assistant.name,
                label: t(assistant.name),
                item: assistant
              }))}
              variant="bordered"
            />
          </div>
        </div>
      )}

      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
          height: showAssistantDropdown ? 'calc(100% - 60px)' : '100%'
        }}>
        {/* Chat area with message bubbles - tylko jeśli thread istnieje */}
        {(() => {
          const useStructured = isWiseAiEnabled() && structuredMessages && structuredMessages.length > 0;
          const hasAnything = useStructured || messages.length > 0;
          if (!hasAnything) return null;
          return (
            <div ref={chatDivRef} className={styles.chatArea}>
              {useStructured ? (
                <WiseAiMessages
                  messages={structuredMessages}
                  includeDetails={includeDetails}
                />
              ) : (
                messages.map((message: ParsedMessage) => (
                  <MessageBubble key={message.id} message={message} />
                ))
              )}
              {isProcessing && (
                <div className={styles.loadingIndicator}>
                  <div className={styles.loadingBubble}>
                    <div className={styles.loadingDot}></div>
                    <div className={styles.loadingDot}></div>
                    <div className={styles.loadingDot}></div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Input area */}
        <div className={classnames(styles.inputArea, { [styles.fullHeight]: !messages.length })}>
          <textarea
            ref={textAreaRef}
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('Type your question or message here...')}
            maxLength={1000}
          />

          <div className={styles.buttonToolbar}>
            <button
              className={classnames(styles.toolbarButton, styles.dangerButton)}
              onClick={startNewThread}
              disabled={!conversationContent.trim()}
              title={t('Clear conversation')}>
              <Trash size={16} />
            </button>

            <button
              className={styles.toolbarButton}
              onClick={removeLastMessage}
              disabled={!conversationContent.trim() && !userMessage.trim()}
              title={t('Remove last message')}>
              <ArrowCounterclockwise size={16} />
            </button>

            {canCurrentAssistantAttachFiles() && (
              <button
                className={styles.toolbarButton}
                onClick={() => setShowFileUpload(!showFileUpload)}
                title={t('Attach files')}>
                <Paperclip size={16} />
                {attachedFilesCount > 0 && (
                  <span className={styles.badge}>{attachedFilesCount}</span>
                )}
              </button>
            )}

            {isDeveloperMode && (
              <label className={styles.developerMode}>
                <input
                  type="checkbox"
                  checked={includeDetails}
                  onChange={() => {
                    setIncludeDetails(!includeDetails);
                    loadHistory();
                  }}
                />
                <span>Dev mode</span>
              </label>
            )}

            {!isProcessing ? (
              <button
                className={styles.primaryButton}
                onClick={handleSendClick}
                disabled={!userMessage.trim() && attachedFiles.length === 0}>
                <Send size={16} />
                {t('Send')}
              </button>
            ) : (
              <button className={styles.primaryButton} onClick={stopProcessing}>
                <Stop size={16} />
                {t('Stop')}
              </button>
            )}
          </div>

          {canCurrentAssistantAttachFiles() && showFileUpload && (
            <div className={styles.fileUploadArea}>
              <label className={styles.fileInputLabel}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.txt,.csv,.md,.json,.html,.xlsx,.png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  className={styles.fileInput}
                />
                <span className={styles.fileInputButton}>
                  {attachedFiles.length > 0 
                    ? t('Change file') + (attachedFiles.length > 1 ? 's' : '') + ` (${attachedFiles.length})`
                    : t('Choose file')}
                </span>
              </label>
              {attachedFiles.length > 0 && (
                <div className={styles.attachedFilesList}>
                  {attachedFiles.map((file, index) => (
                    <div key={index} className={styles.attachedFile}>
                      <span className={styles.fileName}>{file.name}</span>
                      <button
                        className={styles.removeButton}
                        onClick={() => {
                          const newFiles = attachedFiles.filter((_, i) => i !== index);
                          setAttachedFiles(newFiles);
                          handleFileRemoved(file.name);
                        }}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

HydraAIAssistant.displayName = 'HydraAIAssistant';

export default memo(HydraAIAssistant);

